/**
 * Standalone page for Polaris Chat using SimpleChatWidget for plain markdown streaming.
 * Designed to be opened in a popup window.
 * 
 * SimpleChatWidget handles SSE parsing for plain markdown responses from Polaris Chat backend.
 * Backend format: event: text\ndata: line1\ndata: line2\n\n
 * Multiple data: lines are joined with newlines per SSE spec.
 */

import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { SimpleChatWidget } from "@app/features/thesysChat";
import { getAccessToken } from "@app/api/getAccessToken";
import { store } from "@app/redux/store";
import { apiUrl } from "@app/const";

export const PolarisChatPage = () => {
  const { planResultRootId, planResultTypeId } = useParams<{
    planResultRootId: string;
    planResultTypeId: string;
  }>();

  // Build full API URL with base URL prefix
  // Use apiUrl constant to respect environment configuration
  const fullApiUrl = useMemo(() => {
    const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
    // Use Polaris Chat endpoint (still using apiUrl constant for base)
    const chatPath = '/api/polaris-chat';
    const finalUrl = `${baseUrl}${chatPath}${chatPath.endsWith('/') ? '' : '/'}`;
    
    console.log('[PolarisChatPage] API URL configuration:', {
      baseUrl,
      chatPath,
      finalUrl,
      apiUrlConstant: apiUrl,
    });
    
    return finalUrl;
  }, [apiUrl]);

  // Intercept fetch requests to add Authorization header and plan IDs to request body
  // C1Chat makes fetch calls internally - we intercept to add auth and required params
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      
      // Intercept requests to polaris-chat endpoint
      // (matches both relative and absolute URLs)
      const isChatEndpoint = Boolean(
        url.includes('/api/polaris-chat/') && planResultRootId && planResultTypeId
      );
      
      // Debug logging
      if (url.includes('/api/polaris-chat/')) {
        console.log('[PolarisChatPage] Fetch interceptor triggered:', {
          url,
          isChatEndpoint,
          planResultRootId: !!planResultRootId,
          planResultTypeId: !!planResultTypeId,
        });
      }
      
      if (isChatEndpoint) {
        try {
          // Get access token from store
          const accessToken = await getAccessToken(store);
          
          // Add Authorization header if not already present
          const headers = new Headers(init?.headers);
          if (accessToken && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${accessToken}`);
          }
          
          // Ensure Content-Type is set for JSON
          if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
          }
          
          // Parse and modify request body to include plan_result_root_id and plan_result_type_id
          // According to XAI_CHAT_ENDPOINT_GUIDE.md, these are required fields
          let modifiedBody: string | undefined = init?.body as string;
          if (init?.body) {
            try {
              let bodyObj: any;
              if (typeof init.body === 'string') {
                bodyObj = JSON.parse(init.body);
              } else if (init.body instanceof FormData || init.body instanceof Blob) {
                bodyObj = null;
              } else {
                bodyObj = init.body;
              }
              
              if (bodyObj && typeof bodyObj === 'object') {
                if (!bodyObj.plan_result_root_id) {
                  bodyObj.plan_result_root_id = planResultRootId;
                }
                if (!bodyObj.plan_result_type_id) {
                  bodyObj.plan_result_type_id = planResultTypeId;
                }
                modifiedBody = JSON.stringify(bodyObj);
              } else {
                modifiedBody = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
              }
            } catch (e) {
              console.warn('Could not parse request body for chat endpoint:', e);
              modifiedBody = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
            }
          }
          
          // Make request with modified headers and body
          return originalFetch(input, {
            ...init,
            headers,
            body: modifiedBody,
          });
        } catch (error) {
          console.error('Failed to get access token:', error);
          return originalFetch(input, init);
        }
      }
      
      return originalFetch(input, init);
    };
    
    return () => {
      window.fetch = originalFetch;
    };
  }, [planResultRootId, planResultTypeId]);

  if (!planResultRootId || !planResultTypeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="par02Regular">
          Missing required parameters: planResultRootId and planResultTypeId
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* SimpleChatWidget handles SSE parsing for plain markdown from Polaris Chat backend.
          Backend format: event: text\ndata: line1\ndata: line2\n\n
          Multiple data: lines are joined with newlines per SSE spec. */}
      <SimpleChatWidget 
        apiUrl={fullApiUrl}
        planResultRootId={planResultRootId}
        planResultTypeId={planResultTypeId}
      />
    </Box>
  );
};
